import { useCallback, useEffect, useState } from "react";
import { Heart, Pin, PinOff, Plus, Trash2, CalendarClock, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import FlyerComposer from "@/components/flyers/FlyerComposer";

// Admin → Flyers: every announcement (active + expired) with heart counts, pin/unpin, delete.
export default function FlyersTab() {
  const [items, setItems] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/announcements").then(({ data }) => setItems(data?.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pin = async (it) => {
    try { await api.patch(`/announcements/${it.id}`, { pinned: !it.pinned }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update."); }
  };
  const publishNow = async (it) => {
    try { await api.patch(`/announcements/${it.id}`, { publish_now: true }); toast.success("Published."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't publish."); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title}"?`)) return;
    try { await api.delete(`/announcements/${it.id}`); toast.success("Flyer removed."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete."); }
  };

  const totalHearts = (items || []).reduce((n, i) => n + (i.hearts || 0), 0);
  return (
    <div data-testid="admin-flyers-tab">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="text-sm text-zinc-400" data-testid="admin-flyers-summary">{items ? `${items.length} flyers · ${totalHearts} hearts` : "Loading…"}</div>
        <button type="button" onClick={() => setComposerOpen(true)} data-testid="admin-flyer-new-btn"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-1.5 bg-amber-400 text-black hover:bg-amber-300 transition-colors"><Plus size={13} /> Post flyer</button>
      </div>
      {items && items.length === 0 && <div className="text-sm text-zinc-500" data-testid="admin-flyers-empty">No flyers yet.</div>}
      <div className="space-y-2">
        {(items || []).map((it) => (
          <div key={it.id} data-testid={`admin-flyer-row-${it.id}`} className={`flex items-center gap-3 border p-3 bg-zinc-950/60 ${it.expired ? "opacity-60 border-white/5" : it.scheduled ? "border-sky-400/40" : it.pinned ? "border-amber-400/40" : "border-white/10"}`}>
            {it.image_url ? <img src={it.image_url} alt="" className="w-14 h-14 object-cover shrink-0" /> : <div className="w-14 h-14 bg-white/5 shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">{it.title}</div>
              <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-3">
                <span>{new Date(it.created_at).toLocaleDateString()}</span>
                {it.pinned && <span className="text-amber-300">Pinned</span>}
                {it.expired && <span className="text-rose-300">Expired</span>}
                {it.scheduled && <span className="text-sky-300 inline-flex items-center gap-1" data-testid={`admin-flyer-scheduled-${it.id}`}><CalendarClock size={11} /> Scheduled · {new Date(it.publish_at).toLocaleString()}</span>}
                {it.expires_at && !it.expired && <span>Until {new Date(it.expires_at).toLocaleDateString()}</span>}
              </div>
            </div>
            <div className="inline-flex items-center gap-1 text-sm text-rose-300 shrink-0" data-testid={`admin-flyer-hearts-${it.id}`}><Heart size={14} className="fill-current" /> {it.hearts || 0}</div>
            {it.scheduled && <button type="button" onClick={() => publishNow(it)} data-testid={`admin-flyer-publish-now-${it.id}`} title="Publish now" className="h-8 w-8 grid place-items-center bg-white/5 hover:bg-sky-400 hover:text-black text-white transition-colors"><Send size={14} /></button>}
            <button type="button" onClick={() => pin(it)} data-testid={`admin-flyer-pin-${it.id}`} className="h-8 w-8 grid place-items-center bg-white/5 hover:bg-amber-400 hover:text-black text-white transition-colors">{it.pinned ? <PinOff size={14} /> : <Pin size={14} />}</button>
            <button type="button" onClick={() => remove(it)} data-testid={`admin-flyer-delete-${it.id}`} className="h-8 w-8 grid place-items-center bg-white/5 hover:bg-rose-600 text-white transition-colors"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <FlyerComposer open={composerOpen} onOpenChange={setComposerOpen} onCreated={load} />
    </div>
  );
}
